import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/finally';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { ToastrAlertHelper } from 'app/core/helpers/toastr-alert-helper-service';
import { GridLayoutMine, PublicGridLayout } from './grid-layout-menu.interface';
import { GridLayoutMenuService } from './grid-layout-menu.service';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { GridLayoutContext, GridLayoutStore } from './grid-layout-store';

const DEFAULT_BUTTON_CLASS = 'm-btn m-btn-secundary m-btn-circle m-btn-circle-small-medium';
const CONFIRMATION_DIALOG = '.swal2-container';
const SAVED_DATE: Intl.DateTimeFormatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
const SAVED_TIME: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

@Component({
  selector: 'app-grid-layout-menu',
  templateUrl: './grid-layout-menu.component.html',
  styleUrls: ['./grid-layout-menu.component.scss']
})
export class GridLayoutMenuComponent implements OnInit, OnDestroy {

  @ViewChild('layoutMenu') layoutMenu: ElementRef;

  title = '';
  menuOpen = false;
  saving = false;
  mine: GridLayoutMine = { haslayout: false, ispublic: false };
  publicLayouts: PublicGridLayout[] = [];
  private context: GridLayoutContext | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private service: GridLayoutMenuService,
    private toastr: ToastrAlertHelper,
    private sweetAlert: SweetAlertHelper,
    private store: GridLayoutStore,
    private loading: T2LoadingService
  ) { }

  get available(): boolean {
    return !!(this.context && this.context.grid && this.context.moduleActionId);
  }

  get buttonClass(): string {
    return (this.context && this.context.buttonClass) || DEFAULT_BUTTON_CLASS;
  }

  ngOnInit() {
    this.listenStore();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as Element;
    if (!this.menuOpen || !this.layoutMenu || this.layoutMenu.nativeElement.contains(target)) {
      return;
    }
    if (target && target.closest && target.closest(CONFIRMATION_DIALOG)) {
      return;
    }
    this.menuOpen = false;
  }

  toggleMenu() {
    this.menuOpen = !this.menuOpen;
    if (this.menuOpen) {
      this.loadLists();
    }
  }

  inUseName(): string {
    return this.mine && this.mine.ispublic && this.mine.publicname ? this.mine.publicname : 'My Layout';
  }

  inUseMeta(): string {
    const saved = this.mine && this.mine.haslayout && this.mine.modifiedon ? new Date(this.mine.modifiedon) : null;
    if (!saved || isNaN(saved.getTime())) {
      return 'Not saved yet';
    }
    return 'saved ' + saved.toLocaleDateString('en-US', SAVED_DATE) + ' ' + saved.toLocaleTimeString('en-US', SAVED_TIME);
  }

  restoreDefault() {
    const context = this.context;
    if (this.saving || !context || !context.moduleActionId || !context.grid || !context.grid.columnApi) {
      return;
    }
    context.grid.columnApi.resetColumnState();
    this.save('Layout restored to default');
    this.menuOpen = false;
  }

  saveCurrent() {
    this.save('Update your preferences to this grid');
  }

  onApply(item: PublicGridLayout) {
    this.store.requestApply(item);
  }

  onUnpublish(event: Event, item: PublicGridLayout) {
    event.stopPropagation();
    this.store.requestUnpublish(item);
  }

  openManage() {
    this.menuOpen = false;
    this.store.openManage();
  }

  private save(message: string) {
    const context = this.context;
    const config = this.store.readConfig();
    if (this.saving || !context || !context.moduleActionId || !config) {
      return;
    }
    this.saving = true;
    this.loading.show();
    this.track(this.service.save(context.moduleActionId, config).finally(() => this.stopSaving()).subscribe({
      next: () => {
        this.toastr.success(message, 'Success');
        this.loadLists();
      },
      error: error => this.sweetAlert.captureException(error)
    }));
  }

  private stopSaving() {
    this.saving = false;
    this.loading.hiden();
  }

  private loadLists() {
    const context = this.context;
    if (!context || !context.moduleActionId) {
      return;
    }
    this.track(this.service.getMine(context.moduleActionId).subscribe({
      next: data => {
        this.mine = data || { haslayout: false, ispublic: false };
      },
      error: error => this.sweetAlert.captureException(error)
    }));
    this.track(this.service.getPublic(context.moduleActionId).subscribe({
      next: data => {
        this.publicLayouts = data || [];
      },
      error: error => this.sweetAlert.captureException(error)
    }));
  }

  private listenStore() {
    this.track(this.store.context$.subscribe({
      next: context => {
        this.context = context;
        this.title = context ? context.title : '';
      }
    }));
    this.track(this.store.changed$.subscribe({
      next: () => {
        if (this.menuOpen) {
          this.loadLists();
        }
      }
    }));
  }

  private track(subscription: Subscription) {
    this.subscriptions = this.subscriptions.filter(item => !item.closed);
    this.subscriptions.push(subscription);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }
}



// WEBPACK FOOTER //
// ./src/app/utils/grid-layout-menu/grid-layout-menu.component.ts